import { observable, makeObservable, action } from 'mobx';

import { StateManager } from './Manager';

interface MessageManagerState {
    isOpen: boolean;
    duration: number;
    preventClose: boolean;
    onClick: (() => void) | null;

    modalIsOpen: boolean;
    modalCloseLabel: string;
    onCloseModal: undefined | (() => Promise<boolean>);

    justComponent: boolean;
    onUndo: (() => void) | null;
    onClose: (() => void) | null;
}

export class MessageManager extends StateManager<MessageManagerState> {
    protected autoCloser: number | undefined;

    @observable.ref
    public message: React.ReactNode = null;

    @observable.ref
    public modalMessage: React.ReactNode = null;

    constructor() {
        super();
        this.close = this.close.bind(this);
        this.showMessage = this.showMessage.bind(this);
        this.showModalMessage = this.showModalMessage.bind(this);
        this.closeModal = this.closeModal.bind(this);

        this.hideOnNextTouch = this.hideOnNextTouch.bind(this);
        this.hideOnTouch = this.hideOnTouch.bind(this);
        makeObservable(this);
    }

    @action
    public close(): void {
        clearTimeout(this.autoCloser);
        this.updateState({isOpen: false, preventClose: false, onClick: null, justComponent: false});
        this.message = null;
        this.modalMessage = null;
    }

    /**
     *
     * @param message
     * @param duration default 4000
     * @param preventClose default false
     * @param onClick default null
     */
    @action
    public showMessage(message: React.ReactNode, duration: number = 4000, preventClose: boolean = false, onClick: (() => void) | null = null, onUndo: (() => void) | null = null): void {
        clearTimeout(this.autoCloser);
        this.message = message;
        this.updateState({
            isOpen: true,
            duration,
            preventClose,
            onClick: onClick == null ? this.close : onClick,
            justComponent: false,
            onUndo: onUndo
        });
    }

    /**
     *
     * @param message
     * @param onUndo default null
     */
    @action
    public showMessageWithUndo(message: React.ReactNode, onUndo: () => void, onClose?: () => void): void {
        clearTimeout(this.autoCloser);
        this.message = message;

        const close: () => void = () => {
            onClose?.();
            this.close();
        };

        this.updateState({
            isOpen: true,
            duration: 4000,
            preventClose: false,
            justComponent: false,
            onClick: close,
            onUndo:  onUndo,
            onClose: close,
        });

        this.autoCloser = window.setTimeout(close, 4000);
    }

    /**
     * Components/messages shown by this callback will not handle user interactions
     * @param message
     * @param duration
     * @param preventClose
     * @param onClick
     */
     @action
     public showComponent(message: React.ReactNode, duration: number = 4000, preventClose: boolean = false, onClick: (() => void) | null = null): void {
        clearTimeout(this.autoCloser);
        this.message = message;
        this.updateState({
            isOpen: true,
            duration,
            preventClose,
            onClick: onClick == null ? this.close : onClick,
            justComponent: true,
        });
        this.autoCloser = window.setTimeout(this.close, duration);
    }

    @action
    public showModalMessage(modalMessage: React.ReactNode, modalCloseLabel: string, onCloseModal: () => Promise<boolean>): void {
        clearTimeout(this.autoCloser);
        this.modalMessage = modalMessage;
        this.updateState({
            modalCloseLabel,
            onCloseModal,
            justComponent: false,
            modalIsOpen: true,
            onClick: null,
            preventClose: false,
        });
    }

    @action
    public async closeModal(useOnCloseModal: boolean = true): Promise<void> {
        clearTimeout(this.autoCloser);

        let willClose: boolean = true;

        if (this.state.onCloseModal != null && useOnCloseModal) {
            willClose = await this.state.onCloseModal();
        }

        this.updateState({modalIsOpen: !willClose, preventClose: false, onClick: null});

        if (willClose) {
            this.modalMessage = null;
        }
    }

    public hideOnNextTouch(): void {
        window.addEventListener('touchstart', this.hideOnTouch);
        window.addEventListener('mousedown', this.hideOnTouch);
    }

    protected hideOnTouch(): void {
        window.removeEventListener('touchstart', this.hideOnTouch);
        window.removeEventListener('mousedown', this.hideOnTouch);
        this.close();
    }

    protected getInitialState(): MessageManagerState {
        return {
            isOpen: false,
            duration: 4000,
            onClick: null,

            modalIsOpen: false,
            modalCloseLabel: '',
            onCloseModal: undefined,

            preventClose: false,
            justComponent: false,

            onUndo: null,
            onClose: null
        };
    }
}

export default MessageManager;